import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, MapPin, Clock, Loader2, Fingerprint, Calendar, RefreshCw } from "lucide-react";
import {
  createAttendanceRecord,
  getServerTime,
} from "../../../api/hrm/employees/attendanceLog";
import {
  showSuccessToast,
  showErrorToast,
} from "../../../components/ConfirmDialog";
import { AnimatePresence, motion } from "framer-motion";

import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const labelClass =
  "text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1";
const inputClass =
  "w-full bg-white border border-gray-300 rounded-2xl px-4 py-3 text-sm font-medium text-gray-800 outline-none";

const AttendanceLogCreateModal = ({ isOpen, onClose, onSaved }) => {
  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;

  const [loading, setLoading] = useState(false);
  const [workDate, setWorkDate] = useState("");
  const [checkInTime, setCheckInTime] = useState("");
  const [checkInLocation, setCheckInLocation] = useState("");
  const [hasLocation, setHasLocation] = useState(false);

  const fetchLocation = () => {
    if (!navigator.geolocation) {
      showErrorToast("Trình duyệt không hỗ trợ định vị");
      setHasLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setCheckInLocation(
          `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
        );
        setHasLocation(true);
      },
      (err) => {
        console.error("Không thể lấy định vị:", err);
        setCheckInLocation("Không xác định");
        setHasLocation(false);

        if (err.code === 1) {
          showErrorToast(
            "Ứng dụng chưa có quyền truy cập vị trí, vui lòng cấp quyền lại"
          );
        }
      }
    );
  };

  useEffect(() => {
    if (!isOpen) return;

    getServerTime()
      .then(({ date, time }) => {
        setWorkDate(date);
        setCheckInTime(time);
      })
      .catch((err) => {
        console.error("Lỗi lấy giờ server:", err);
        showErrorToast("Không thể lấy giờ hệ thống từ server");
      });

    fetchLocation();
  }, [isOpen]);

  const handleSave = async () => {
    if (!user?.id) return showErrorToast("Không tìm thấy thông tin nhân viên");
    if (!workDate || !checkInTime || !checkInLocation)
      return showErrorToast("Thiếu dữ liệu chấm công");

    const payload = {
      employee_id: user.id,
      work_date: workDate,
      check_in_time: checkInTime,
      check_in_location: checkInLocation,
    };

    try {
      setLoading(true);
      await createAttendanceRecord(payload);
      showSuccessToast("Chấm công thành công!");
      onSaved?.();
      onClose();
    } catch (error) {
      console.error("Lỗi khi chấm công:", error);
      showErrorToast("Không thể chấm công");
    } finally {
      setLoading(false);
    }
  };

  const mapCenter =
    checkInLocation && checkInLocation !== "Không xác định"
      ? checkInLocation.split(",").map((v) => parseFloat(v.trim()))
      : null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[200] bg-black/45 backdrop-blur-sm"
            onClick={onClose}
          />

          <div className="fixed inset-0 z-[210] flex items-end justify-center p-0 pointer-events-none sm:items-center sm:p-4">
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 320 }}
              className="pointer-events-auto flex h-[90dvh] w-full max-w-3xl flex-col overflow-hidden rounded-t-[1.75rem] border border-gray-200 bg-white shadow-2xl sm:h-auto sm:max-h-[90vh] sm:rounded-[2rem]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 bg-gradient-to-r from-red-50/70 via-white to-white px-5 py-4 sm:px-6">
                <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                  <div className="rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm">
                    <Fingerprint
                      className="h-5 w-5 sm:h-6 sm:w-6"
                      strokeWidth={2.25}
                    />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg font-black uppercase tracking-tight text-gray-800 sm:text-xl">
                      Đăng ký <span className="text-red-700">chấm công</span>
                    </h2>
                    <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
                      Giờ hệ thống & vị trí GPS hiện tại
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl border border-gray-200 bg-white p-2 text-gray-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                  aria-label="Đóng"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6">
                <section className="space-y-4 rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50/80 to-white p-4 sm:p-5">
                  <h3 className="text-[11px] font-black uppercase tracking-widest text-gray-500">
                    Thông tin chấm công
                  </h3>

                  <div>
                    <label className={`${labelClass} mb-1.5 block`}>Nhân viên</label>
                    <input
                      type="text"
                      disabled
                      value={user?.name || "Chưa đăng nhập"}
                      className={`${inputClass} bg-slate-50 text-gray-600`}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className={`${labelClass} mb-1.5 flex items-center gap-1.5`}>
                        <Calendar size={12} className="text-red-500" />
                        Ngày làm việc
                      </label>
                      <input
                        type="text"
                        disabled
                        value={workDate || "Đang tải..."}
                        className={`${inputClass} bg-slate-50 text-gray-600`}
                      />
                    </div>
                    <div>
                      <label className={`${labelClass} mb-1.5 flex items-center gap-1.5`}>
                        <Clock size={12} className="text-red-500" />
                        Giờ vào
                      </label>
                      <div className={`${inputClass} flex items-center gap-2 bg-slate-50 text-gray-600`}>
                        <Clock size={14} className="text-gray-400" />
                        <span>{checkInTime || "Đang tải..."}</span>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="space-y-4 rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50/80 to-white p-4 sm:p-5">
                  <h3 className="text-[11px] font-black uppercase tracking-widest text-gray-500">
                    Vị trí
                  </h3>

                  <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-start gap-2">
                      <MapPin size={16} className="mt-0.5 shrink-0 text-red-500" />
                      <div className="min-w-0">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                          Tọa độ GPS
                        </p>
                        <p className="truncate text-sm font-medium text-gray-800">
                          {checkInLocation || "Đang xác định..."}
                        </p>
                      </div>
                    </div>
                    {!hasLocation ? (
                      <button
                        type="button"
                        onClick={() => {
                          navigator.geolocation.getCurrentPosition(
                            (pos) => {
                              const { latitude, longitude } = pos.coords;
                              setCheckInLocation(
                                `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
                              );
                              setHasLocation(true);
                              showSuccessToast("Đã lấy lại vị trí thành công");
                            },
                            (err) => {
                              console.error("Không thể lấy định vị:", err);
                              showErrorToast(
                                "Không thể lấy vị trí, hãy bật định vị"
                              );
                            }
                          );
                        }}
                        className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full border border-sky-100 bg-sky-50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-sky-700"
                      >
                        <RefreshCw size={12} />
                        Cấp quyền lại
                      </button>
                    ) : (
                      <span className="inline-flex shrink-0 rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-emerald-700">
                        Đã định vị
                      </span>
                    )}
                  </div>

                  {mapCenter &&
                  Number.isFinite(mapCenter[0]) &&
                  Number.isFinite(mapCenter[1]) ? (
                    <div>
                      <label className={`${labelClass} mb-1.5 block`}>
                        Bản đồ vị trí
                      </label>
                      <div className="h-48 overflow-hidden rounded-2xl border border-gray-200 sm:h-52">
                        <MapContainer
                          key={checkInLocation}
                          center={mapCenter}
                          zoom={16}
                          style={{ height: "100%", width: "100%" }}
                          scrollWheelZoom={false}
                        >
                          <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution="© OpenStreetMap"
                          />
                          <Marker
                            position={mapCenter}
                            icon={
                              new L.Icon({
                                iconUrl:
                                  "https://maps.gstatic.com/mapfiles/ms2/micons/green-dot.png",
                                iconSize: [32, 32],
                                iconAnchor: [16, 32],
                              })
                            }
                          />
                        </MapContainer>
                      </div>
                    </div>
                  ) : null}
                </section>
              </div>

              <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50/80 px-5 py-4 sm:flex-row sm:justify-end sm:gap-3 sm:px-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full border border-gray-200 bg-white px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-gray-600 transition hover:border-gray-300 hover:bg-gray-50"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-red-700 to-red-800 px-6 py-2.5 text-xs font-black uppercase tracking-wider text-white shadow-md shadow-red-100 transition hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Đang lưu...
                    </>
                  ) : (
                    "Chấm công"
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default AttendanceLogCreateModal;
